import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";

import useStyles from "./styles";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { useHasPermission } from "../../auth/useAuth";
import { useGetParameter, useUpdateParameter } from "../../api/parametre";
import { useGetAllEtablissements } from "../../api/etablissement";
import { ModalAddEtablissement } from "./ModalAddEtablissement/ModalAddEtablissement";
import { ModalEditEtablissement } from "./ModalEditEtablissement/ModalEditEtablissement";
import { ModalDeleteEtablissement } from "./ModalDeleteEtablissement/ModalDeleteEtablissement";

type Etablissement = { id: number; name: string; signs?: any[]; created_at?: string };

/**
 * Platform parameters + établissements.
 *
 * Rewritten for density and honesty about permissions. The previous version
 * put every toggle behind a full-screen blocking loader, rendered descriptions
 * with a heading component, and showed the same controls to users who were not
 * allowed to use them.
 */
export const Parameter: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  // The API enforces this; mirroring it here means a user without the
  // permission sees read-only switches instead of controls that 403 on click.
  const canManage = useHasPermission("platform.parameters.manage");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<Etablissement | null>(null);

  /*
   * Debounced search.
   *
   * The search term is part of the query key, so every keystroke previously
   * fired its own request: typing "Toulouse" was eight round trips, the list
   * flickered through partial results, and responses could land out of order
   * and leave the wrong ones on screen. 350ms is below the threshold where
   * typing feels laggy and collapses a word into one request.
   */
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data: parameter, isLoading: loadingParameter, error: parameterError } =
    useGetParameter();

  const { mutate: updateParameter, isPending: savingParameter } =
    useUpdateParameter();

  const {
    etablissements,
    pagination,
    isLoading: loadingEtablissements,
    refetch: refetchEtablissements,
  } = useGetAllEtablissements({
    page: page + 1, // API is 1-based, MUI is 0-based
    per_page: rowsPerPage,
    search: searchTerm,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const hasParameterError = Boolean(parameterError) && !loadingParameter;

  const settings = useMemo(
    () => [
      {
        key: "editable" as const,
        checked: parameter?.editable ?? false,
        title: t("parameter.editable", "Modification des événements"),
        description: t(
          "parameter.editableDescription",
          "Autoriser les utilisateurs à modifier un événement existant."
        ),
      },
      {
        key: "supprimable" as const,
        checked: parameter?.supprimable ?? false,
        title: t("parameter.supprimable", "Suppression des événements"),
        description: t(
          "parameter.supprimableDescription",
          "Autoriser les utilisateurs à supprimer un événement existant."
        ),
      },
    ],
    [parameter, t]
  );

  // Only the changed flag is sent. Echoing the other one back from a stale
  // read is how two administrators on this screen overwrite each other.
  const toggle = (key: "editable" | "supprimable", value: boolean) =>
    updateParameter({ [key]: value });

  const handleSort = (property: string) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
    setPage(0);
  };

  const columns: Array<{ id: string; label: string; sortable: boolean; align?: "right" }> = [
    { id: "name", label: t("parameter.name", "Nom"), sortable: true },
    { id: "signs", label: t("parameter.signs", "Enseignes"), sortable: false },
    { id: "created_at", label: t("parameter.createdAt", "Créé le"), sortable: true },
    { id: "actions", label: t("parameter.actions", "Actions"), sortable: false, align: "right" },
  ];

  return (
    <>
      <ModalAddEtablissement
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={refetchEtablissements}
      />
      <ModalEditEtablissement
        open={editOpen}
        onClose={() => setEditOpen(false)}
        etablissement={selected}
        onSuccess={refetchEtablissements}
      />
      <ModalDeleteEtablissement
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        etablissement={selected}
        onSuccess={refetchEtablissements}
      />

      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <Box className={classes.pageContainer}>
          {/* ── Header ─────────────────────────────────────────────── */}
          <Box mb={3}>
            <Typography variant="h5" fontWeight={700}>
              {t("parameter.parameterManagement", "Paramètres")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t(
                "parameter.pageSubtitle",
                "Règles globales de la plateforme et liste des établissements."
              )}
            </Typography>
          </Box>

          {/* ── Settings ───────────────────────────────────────────── */}
          <Paper variant="outlined" className={classes.card}>
            <Box className={classes.cardHead}>
              <Box>
                <Typography fontWeight={600}>
                  {t("parameter.settings", "Événements")}
                </Typography>
                {/*
                  These flags are not tenant-scoped: one row governs every
                  company. Saying so next to the switches is the difference
                  between an informed change and a surprise.
                */}
                <Typography variant="caption" color="text.secondary">
                  {t(
                    "parameter.settingsScope",
                    "S'applique à toutes les sociétés de la plateforme."
                  )}
                </Typography>
              </Box>
              {savingParameter && <CircularProgress size={16} />}
            </Box>

            {hasParameterError ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {t("parameter.errorLoading", "Impossible de charger les paramètres.")}
              </Alert>
            ) : (
              <>
                {!canManage && (
                  <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
                    {t(
                      "parameter.readOnly",
                      "Consultation seule : vous n'avez pas la permission de modifier ces paramètres."
                    )}
                  </Alert>
                )}

                <Stack divider={<Divider />}>
                  {settings.map((s) => (
                    <Box key={s.key} className={classes.settingRow}>
                      <Box minWidth={0} pr={2}>
                        {/* Body text, not a heading. Both lines used to render
                            as <h4>, so the description carried the same visual
                            and semantic weight as the label — and a screen
                            reader announced a heading for every sentence. */}
                        <Typography fontSize={14} fontWeight={600}>
                          {s.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.description}
                        </Typography>
                      </Box>

                      {loadingParameter ? (
                        <Skeleton variant="rounded" width={34} height={20} />
                      ) : (
                        <Switch
                          checked={s.checked}
                          onChange={(e) => toggle(s.key, e.target.checked)}
                          disabled={!canManage || savingParameter}
                          // Named for assistive tech: the visible label is a
                          // sibling element, so without this the control is
                          // announced only as "switch".
                          inputProps={{ "aria-label": s.title }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Paper>

          {/* ── Établissements ─────────────────────────────────────── */}
          <Paper variant="outlined" className={classes.card}>
            <Box className={classes.cardHead}>
              <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography fontWeight={600}>
                  {t("parameter.etablissementTable", "Établissements")}
                </Typography>
                {pagination && (
                  <Chip size="small" label={pagination.total} variant="outlined" />
                )}
              </Stack>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {t("parameter.add", "Ajouter")}
              </Button>
            </Box>

            <Box px={2} pb={2}>
              <TextField
                fullWidth
                size="small"
                placeholder={t(
                  "parameter.searchEtablissements",
                  "Rechercher un établissement…"
                )}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setSearchInput("")}
                        aria-label={t("parameter.clearSearch", "Effacer la recherche")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell
                        key={c.id}
                        align={c.align}
                        className={classes.headCell}
                      >
                        {c.sortable ? (
                          <TableSortLabel
                            active={sortBy === c.id}
                            direction={sortBy === c.id ? sortOrder : "asc"}
                            onClick={() => handleSort(c.id)}
                          >
                            {c.label}
                          </TableSortLabel>
                        ) : (
                          c.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loadingEtablissements ? (
                    /* Skeleton rows rather than a single "Loading…" cell: the
                       table keeps its height, so the pagination below does not
                       jump every time a page or a search resolves. */
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        {columns.map((c) => (
                          <TableCell key={c.id} align={c.align}>
                            <Skeleton width={c.id === "actions" ? 60 : "70%"} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : etablissements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <Stack alignItems="center" spacing={1.5} py={5}>
                          <BusinessIcon color="disabled" />
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm
                              ? t("parameter.noSearchResults", "Aucun résultat pour cette recherche.")
                              : t("parameter.noEtablissements", "Aucun établissement pour le moment.")}
                          </Typography>
                          {/* An empty state that only says "empty" leaves the
                              user to find the button themselves. */}
                          {searchTerm ? (
                            <Button size="small" onClick={() => setSearchInput("")}>
                              {t("parameter.clearSearch", "Effacer la recherche")}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => setAddOpen(true)}
                            >
                              {t("parameter.add", "Ajouter")}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    etablissements.map((e: Etablissement) => (
                      <TableRow key={e.id} hover>
                        <TableCell>
                          <Typography fontSize={13.5} fontWeight={500}>
                            {e.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} color="text.secondary">
                            {e.signs?.length ?? 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} color="text.secondary">
                            {e.created_at
                              ? new Date(e.created_at).toLocaleDateString()
                              : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={t("parameter.edit", "Modifier")}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelected(e);
                                  setEditOpen(true);
                                }}
                                aria-label={`${t("parameter.edit", "Modifier")} ${e.name}`}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("parameter.delete", "Supprimer")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelected(e);
                                  setDeleteOpen(true);
                                }}
                                aria-label={`${t("parameter.delete", "Supprimer")} ${e.name}`}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && pagination.total > 0 && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t("parameter.rowsPerPage", "Lignes :")}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t("parameter.of", "sur")} ${count}`
                }
                className={classes.pagination}
              />
            )}
          </Paper>
        </Box>
      </Sidebar>
    </>
  );
};
